import React from 'react';
import styled from 'styled-components';
import { gql } from 'apollo-boost';
import { useQuery } from 'react-apollo';
import { themes } from 'kb-shared';
import { AppointmentType, Clinic, ReduxState } from 'kb-shared';
import HelpTextButton from '../../components/HelpTextButton';
import HelpText from '../../components/HelpText';
import { connect } from 'react-redux';
import qs from 'qs';
import { QUERY_MEMBERSHIP } from 'kb-shared/graphql/queries';
import { QueryMembershipResponse } from 'kb-shared/graphql/queries_types';
import InPersonLabSelectorInput from './InPersonLabSelectorInput';
import LocationCards from './LocationCards';
import moment from 'moment';
import { utils } from 'kb-shared';
import { AdditionalInfoPanel } from 'screens/Book/components/AdditionalInfoPanel';

export type Lab = {
  id: string;
  name: string;
  stripePublicKey: string;
  vios: boolean;
};

const LABS_QUERY = gql`
  query labs {
    labs {
      id
      name
      stripePublicKey
      vios
    }
  }
`;

interface LabsQuery {
  labs: Array<Lab>;
}

const CLINICS_QUERY = gql`
  query clinics($appointmentTypeId: Int!, $labId: Int!, $startDateTime: String!) {
    hasTimeSlots(
      appointmentTypeId: $appointmentTypeId
      labId: $labId
      startDateTime: $startDateTime
    )
    clinics: locations(appointmentTypeId: $appointmentTypeId, labId: $labId) {
      id
      name
      address
      timeZone
      imageUrl
      patientFacing
    }
  }
`;

interface ClinicsQuery {
  clinics: Array<Clinic>;
  hasTimeSlots: boolean;
}

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 24px;
  width: 100%;
  max-width: 100%;
  @media screen and (max-width: ${themes.breakpoints.largePhonePortrait}) {
    min-width: 100%;
  }
`;

const LabSelectorContainer = styled.div`
  display: flex;
  align-items: center;
  padding-top: 20px;
  @media screen and (max-width: ${themes.breakpoints.largePhonePortrait}) {
    flex-direction: column;
    min-width: 100%;
    padding-top: 0px;
  }
`;

const UnableToFindContainer = styled.div`
  margin: 40px 0px;
  display: flex;
  flex-direction: column;
  align-items: center;
  div {
    text-align: center;
  }

  > * {
    padding: 20px 0px;
  }
`;

const Title = styled.div`
  ${themes.fonts.DomaineDisplay};
  font-size: 24px;
  color: ${themes.colors.KBNight};
`;

interface Props {
  appointmentType?: AppointmentType;
  selectedLab: Lab | null;
  selectedClinic: Clinic | null;
  onSelectLab: (lab: Lab) => void;
  onSelectClinic: (clinic: Clinic | null) => void;
  onSelectVirtual: () => void;
  onContactUsClick: () => void;
  directToPartnerClinicSearch: boolean;
  showVirtual: boolean;
  isLoggedIn?: boolean;
  patientMembership?: {
    membershipId?: string;
    employer: boolean;
  };
}

const ADMIN_MESSAGE = {
  category: 'admin',
  subject: 'New Appointment Question',
  message: ''
};

const { PRINCETON: PRINCETON_LAB_ID } = utils.labs(process.env.REACT_APP_ENV);

const SelectLocationVirtual = (props: Props) => {
  const {
    selectedLab,
    onSelectLab,
    selectedClinic,
    onSelectClinic,
    onSelectVirtual,
    onContactUsClick,
    directToPartnerClinicSearch,
    appointmentType,
    showVirtual,
    isLoggedIn,
    patientMembership
  } = props;
  const { data: labsData } = useQuery<LabsQuery>(LABS_QUERY);

  const { data: clinicsData, loading } = useQuery<ClinicsQuery>(CLINICS_QUERY, {
    skip: !selectedLab || !appointmentType,
    variables: {
      appointmentTypeId: appointmentType ? parseInt(appointmentType.id) : undefined,
      labId: selectedLab ? parseInt(selectedLab.id) : undefined,
      startDateTime: moment.utc().startOf('month')
    }
  });

  const { data: membershipData } = useQuery<QueryMembershipResponse>(QUERY_MEMBERSHIP, {
    variables: { membershipId: patientMembership?.membershipId },
    skip: !patientMembership?.membershipId
  });

  const clinics = clinicsData && clinicsData.clinics;
  const clinicsHasAppoinments = clinicsData && clinicsData.hasTimeSlots;

  if (!labsData || !labsData.labs) {
    return null;
  }

  const showPartnerClinicList =
    directToPartnerClinicSearch &&
    patientMembership &&
    patientMembership.membershipId &&
    patientMembership.employer &&
    !membershipData?.membership.hidePartnerClinics;

  const shouldShowLabSelector = !selectedLab || labsData.labs.length > 1;
  const hasAppointmentsAvailable = clinicsHasAppoinments || showVirtual;
  const shouldShowLocationCards = selectedLab && clinics && hasAppointmentsAvailable;

  const shouldShowSignatureClinicPromo = selectedLab?.id === PRINCETON_LAB_ID;

  return (
    <Container>
      {shouldShowLabSelector && (
        <LabSelectorContainer>
          <InPersonLabSelectorInput
            labs={labsData.labs.filter(lab => !lab.vios)}
            onSelect={onSelectLab}
            selectedLab={selectedLab}
          />
        </LabSelectorContainer>
      )}
      {selectedLab && !hasAppointmentsAvailable && !loading && (
        <UnableToFindContainer>
          <Title>
            We’re sorry, there are currently no time slots available for this appointment.
            <br />
            Please book a virtual appointment or contact us for more information.
          </Title>
        </UnableToFindContainer>
      )}
      {shouldShowLocationCards && (
        <LocationCards
          clinics={clinics}
          clinicsHasAppoinments={!!clinicsHasAppoinments}
          showVirtual={showVirtual}
          selectedClinic={selectedClinic}
          onSelectClinic={onSelectClinic}
          onSelectVirtual={onSelectVirtual}
        />
      )}
      <>
        {shouldShowSignatureClinicPromo && (
          <StyledAdditionalInfoPanel>
            Kindbody’s signature clinic offers a full suite of services in-house. Patients who seek
            fertility treatment through IVF or egg freezing will visit our Bryant Park location in
            New York City the day of their egg retrieval or embryo transfer.
          </StyledAdditionalInfoPanel>
        )}
      </>
      {isLoggedIn && showPartnerClinicList && (
        <HelpTextButton
          linkText="Find a Partner Clinic"
          text={`Looking for another clinic?`}
          onClick={onContactUsClick}
        />
      )}
      {isLoggedIn && !showPartnerClinicList && (
        <HelpText
          linkText="Contact us"
          text={`Don't see your ${selectedLab ? 'clinic' : 'city'}?`}
          linkUrl={`/messages?${qs.stringify(ADMIN_MESSAGE)}`}
        />
      )}
    </Container>
  );
};

const StyledAdditionalInfoPanel = styled(AdditionalInfoPanel)`
  margin-top: 5em;
`;

const mapStateToProps = ({
  patient: {
    isLoggedIn,
    patient: { patientMembership }
  }
}: ReduxState) => ({
  isLoggedIn: isLoggedIn,
  patientMembership: patientMembership
});

export default connect(mapStateToProps, null)(SelectLocationVirtual);
